import { get, divide, isNil } from 'lodash';

import { CAMPAIGN_NAMES } from 'constants/personalizations';
import { MODAL_TYPES } from 'state/constants';
import { getCurrentProduct, getCurrentVariant } from 'state/selectors/pdp';
import PersonalizationsSelector from 'state/selectors/personalizations';
import Device from 'utils/Device';
import Gender from 'utils/Gender';
import Iterable from 'utils/Iterable';
import Rudderstack from 'utils/Rudderstack';
import Settings from 'utils/Settings';

const openSizeChartEvent = (action, prevState, nextState) => {
  const modalType = get(action, 'payload');

  if (modalType === MODAL_TYPES.PDP_SIZE_CHART) {
    const currentProduct = getCurrentProduct(nextState);
    const currentVariant = getCurrentVariant(nextState);

    const color = get(currentProduct, 'colorName');
    const gender = get(currentProduct, 'formattedGender');
    const formattedGender = Gender.getPluralGender(gender);
    const genericProductName = get(currentProduct, 'genericProductName');

    const size = get(currentVariant, 'value', null);
    const price = get(currentVariant, 'price');
    const formattedPrice = isNil(price) ? null : divide(price, 100);
    const sku = get(currentVariant, 'sku', null);

    return {
      hitType: 'event',
      eventAction: 'Open Size Chart',
      eventCategory: 'PDP',
      eventLabel: 'Size Chart',
      eventValue: 1,
      dimension3: color,
      dimension4: size,
      dimension5: formattedGender,
      dimension6: formattedPrice,
      dimension7: genericProductName,
      dimension8: sku,
    };
  }

  return null;
};

const openProductUpdatesFormEvent = (action, prevState, nextState) => {
  const modalType = get(action, 'payload');

  if (modalType === MODAL_TYPES.PDP_UPDATE_FORM) {
    const currentProduct = getCurrentProduct(nextState);

    return {
      hitType: 'event',
      eventAction: 'openBackInStockModal',
      eventCategory: 'PDP',
      eventLabel: get(currentProduct, 'fullName'),
      eventValue: 1,
    };
  }

  return null;
};

const trackProductUpdatesTooltipView = (action, prevState, nextState) => {
  const currentProduct = getCurrentProduct(nextState);

  return {
    hitType: 'event',
    eventAction: 'viewBackInStockTooltip',
    eventCategory: 'PDP',
    eventLabel: get(currentProduct, 'fullName'),
    eventValue: 1,
  };
};

const signUpForProductUpdates = (action, prevState, nextState) => {
  const origination = get(action, ['meta', 'origination']);
  const emailAddress = get(action, ['payload', 'emailAddress']);
  const pdpSignupFlow = get(action, ['payload', 'pdpSignupFlow']);
  const currentProduct = getCurrentProduct(nextState);
  const currentVariant = getCurrentVariant(nextState);

  Iterable.push({
    email: emailAddress,
    signupSource: origination,
    pdpSignupFlow,
    locale: Settings.currentLocale,
    siteRegion: Settings.shopID,
    deviceType: Device.getDeviceType(),
    productData: {
      name: get(currentProduct, 'masterName'),
      sku: get(currentVariant, 'sku'),
      categories: {
        allbirdsEdition: get(currentProduct, 'edition'),
        allbirdsGender: get(currentProduct, 'gender'),
        allbirdsHue: get(currentProduct, 'colorName'),
        allbirdsMaster: get(currentProduct, 'masterId'),
        allbirdsMaterial: get(currentProduct, 'material'),
        allbirdsSilhouette: get(currentProduct, 'silhouette'),
      },
    },
  });

  Rudderstack.identifyUser({
    email: emailAddress,
    additionalTraits: {
      marketing_email_subscription: true,
      marketing_email_subscription_source: origination,
    },
  });
  Rudderstack.userMarketingPreference({
    ...Rudderstack.defaultMarketingPreferencesPayload(),
    placement: origination,
    optInStatus: true,
    cmsProductId: get(currentProduct, 'id'),
    sku: get(currentVariant, 'sku'),
  });

  return {
    hitType: 'event',
    eventAction: 'submitBackInStockEmail',
    eventCategory: 'PDP',
    eventLabel: get(currentProduct, 'fullName'),
    eventValue: 1,
  };
};

const trackProductSelectorCarouselScroll = (action, prevState, nextState) => {
  const currentProduct = getCurrentProduct(nextState);

  return {
    hitType: 'event',
    eventAction: 'Swipe Color Carousel',
    eventCategory: 'PDP',
    eventLabel: get(currentProduct, 'masterName'),
    eventValue: 1,
  };
};

const trackOpenAttribute = (action, prevState, nextState) => {
  const openAttribute = get(action, ['meta', 'origination']);
  const currentProduct = getCurrentProduct(nextState);
  const openAttributeIndex = get(action, 'payload');
  const shouldFireEvent = !(openAttributeIndex === null);

  return (
    shouldFireEvent && {
      hitType: 'event',
      eventAction: `Expand PDP Dropdown -- ${get(currentProduct, 'masterName')}`,
      eventCategory: 'PDP',
      eventLabel: openAttribute,
    }
  );
};

const trackViewDifferentiatorsClick = () => {
  return {
    hitType: 'event',
    eventCategory: 'PDP',
    eventAction: 'highlights',
    eventLabel: 'view_highlights',
  };
};

const trackCloseAddToCartModal = (action, prevState, nextState) => {
  const type = get(action, ['payload', 'closeType']);
  const name = get(action, ['payload', 'closeLocation']);
  const campaignData = PersonalizationsSelector.getCampaignByName(
    nextState,
    CAMPAIGN_NAMES.PDP_ADD_TO_CART_MODAL_RECS
  );
  const variation = campaignData?.variations?.[0];

  Rudderstack.callToActionClicked({
    location: 'PDP Add to Cart Modal',
    name,
    type,
    variationName: variation?.payload?.data?.custom?.title,
    variationId: variation?.analyticsMetadata?.variationId,
    experienceName: variation?.analyticsMetadata?.experienceName,
    campaignName: campaignData?.name?.[CAMPAIGN_NAMES.PDP_ADD_TO_CART_MODAL_RECS],
  });
};

const trackStoreInventoryDropdownEvent = action => {
  const type = get(action, ['payload', 'type']);
  const name = get(action, ['payload', 'name']);
  const copy = get(action, ['payload', 'copy']);

  Rudderstack.callToActionClicked({
    location: 'PDP Store Inventory Dropdown',
    name,
    type,
    copy,
  });
};

const trackStoreInventoryModalEvent = action => {
  const type = get(action, ['payload', 'type']);
  const name = get(action, ['payload', 'name']);
  const copy = get(action, ['payload', 'copy']);

  Rudderstack.callToActionClicked({
    location: 'PDP Store Inventory Modal',
    name,
    type,
    copy,
  });
};

export {
  openSizeChartEvent,
  openProductUpdatesFormEvent,
  trackProductUpdatesTooltipView,
  signUpForProductUpdates,
  trackProductSelectorCarouselScroll,
  trackOpenAttribute,
  trackViewDifferentiatorsClick,
  trackCloseAddToCartModal,
  trackStoreInventoryDropdownEvent,
  trackStoreInventoryModalEvent,
};

export default openSizeChartEvent;
